import React, { useState } from 'react';
import { 
  Upload, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  Info, 
  Download, 
  Eye, 
  Shield,
  DollarSign,
  XCircle
} from 'lucide-react';

const SingleDocumentCheck = () => {
  const [activeView, setActiveView] = useState('upload');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedSection, setSelectedSection] = useState('declarations');
  const [selectedIssue, setSelectedIssue] = useState(null);

  // Function to get detailed document content for each issue
  const getDocumentContent = (issueId) => {
    switch (issueId) {
      case 1: // Missing Primary & Non-Contributory Endorsement
        return {
          title: "SECTION IV - COMMERCIAL GENERAL LIABILITY CONDITIONS",
          content: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                2. Duties In The Event Of Occurrence, Offense, Claim Or Suit
              </p>
              <p className="text-sm text-gray-700 mb-3">
                a. You must see to it that we are notified as soon as practicable...
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-gray-500 italic">[Missing provision]</span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">MISSING: CG 20 01 Endorsement</p>
                    <p className="text-xs text-red-600 mt-1">This creates coverage gaps and potential carrier disputes</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                b. If a claim is made or suit is brought against any insured...
              </p>
            </div>
          )
        };
      case 2: // Restrictive Additional Insured Form
        return {
          title: "SECTION IV - ADDITIONAL INSURED ENDORSEMENTS",
          content: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                CG 20 10 (04/13) - Additional Insured - Designated Person or Organization
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-red-600 font-medium">
                        Coverage limited to work directly performed by the named insured. Excludes additional insured's own negligence.
                      </span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">RESTRICTIVE FORM</p>
                    <p className="text-xs text-red-600 mt-1">Limits coverage scope and may exclude negligence claims</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This endorsement modifies insurance provided under the following coverages...
              </p>
            </div>
          )
        };
      case 3: // Defective Workmanship Exclusion Gap
        return {
          title: "SECTION III - EXCLUSIONS - COMMERCIAL GENERAL LIABILITY",
          content: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Coverage A - Bodily Injury and Property Damage Liability
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-red-600 font-medium">
                        j. Damage To Your Product - "Property damage" to "your product" arising out of it or any part of it.
                      </span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">EXCLUSION APPLIES</p>
                    <p className="text-xs text-red-600 mt-1">No subcontractor exception - eliminates common construction coverage</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This exclusion does not apply to "property damage" to "your work" arising out of it...
              </p>
            </div>
          )
        };
      case 4: // Inadequate Completed Operations Coverage
        return {
          title: "SECTION III - COVERAGE - COMMERCIAL GENERAL LIABILITY",
          content: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                Coverage A - Bodily Injury and Property Damage Liability
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-orange-600 font-medium">
                        Completed Operations Coverage Period: 2 years from completion
                      </span>
                    </p>
                    <p className="text-xs text-orange-600 font-semibold">INADEQUATE PERIOD</p>
                    <p className="text-xs text-orange-600 mt-1">Defects often appear 3-5 years after completion</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This insurance applies to "bodily injury" and "property damage" only if...
              </p>
            </div>
          )
        };
      case 5: // Missing Waiver of Subrogation
        return {
          title: "SECTION IV - ENDORSEMENTS - WAIVER OF SUBROGATION",
          content: (
            <div>
              <p className="text-sm text-gray-700 mb-3">
                CG 24 26 - Waiver of Transfer of Rights of Recovery Against Others to Us
              </p>
              <div className="bg-red-50 border-l-4 border-red-500 p-3 my-3 rounded-r-lg">
                <div className="flex items-start gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 mb-1">
                      <span className="text-gray-500 italic">[Missing endorsement]</span>
                    </p>
                    <p className="text-xs text-red-600 font-semibold">MISSING: CG 24 26 Endorsement</p>
                    <p className="text-xs text-red-600 mt-1">Allows carrier to seek recovery from additional insureds</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-700">
                This endorsement modifies insurance provided under the following coverages...
              </p>
            </div>
          )
        };
      default:
        return {
          title: "Document Section",
          content: <div className="text-sm text-gray-700">No specific content available for this issue.</div>
        };
    }
  };

  const criticalIssues = [
    {
      id: 1,
      priority: 'critical',
      title: 'Missing Primary & Non-Contributory Endorsement',
      section: 'Conditions',
      description: 'Policy lacks CG 20 01 endorsement, creating coverage priority disputes',
      exposure: '$1.2M',
      recommendation: 'Add CG 20 01 endorsement immediately'
    },
    {
      id: 2,
      priority: 'critical',
      title: 'Restrictive Additional Insured Form',
      section: 'Endorsements',
      description: 'Using CG 20 10 (04/13) instead of broader CG 20 10 (11/85)',
      exposure: '$800K',
      recommendation: 'Upgrade to CG 20 10 (11/85) form'
    },
    {
      id: 3,
      priority: 'critical',
      title: 'Defective Workmanship Exclusion Gap',
      section: 'Exclusions',
      description: 'Subcontractor exception removed, eliminating common coverage',
      exposure: '$400K',
      recommendation: 'Restore subcontractor exception endorsement'
    }
  ];

  const highPriorityIssues = [
    {
      id: 4,
      priority: 'high',
      title: 'Inadequate Completed Operations Coverage',
      section: 'Coverage',
      description: 'Coverage ends 2 years before statute of repose expires',
      exposure: '$300K',
      recommendation: 'Extend coverage through statute of repose period'
    },
    {
      id: 5,
      priority: 'high',
      title: 'Missing Waiver of Subrogation',
      section: 'Conditions',
      description: 'No waiver of subrogation for additional insureds',
      exposure: '$250K',
      recommendation: 'Add waiver of subrogation endorsement'
    },
    {
      id: 6,
      priority: 'high',
      title: 'Pollution Exclusion Too Broad',
      section: 'Exclusions',
      description: 'Excludes mold, fungus, and fuel spills common in construction',
      exposure: '$200K',
      recommendation: 'Add pollution liability coverage or narrow exclusion'
    },
    {
      id: 7,
      priority: 'high',
      title: 'Inadequate Aggregate Limits',
      section: 'Declarations',
      description: 'General aggregate limit may be exhausted by multiple claims',
      exposure: '$150K',
      recommendation: 'Increase aggregate limits or add umbrella coverage'
    }
  ];

  const mediumPriorityIssues = [
    {
      id: 8,
      priority: 'medium',
      title: 'Higher Deductible Amount',
      section: 'Conditions',
      description: 'Deductible increased from $5,000 to $10,000 per occurrence',
      exposure: '$50K',
      recommendation: 'Consider impact on cash flow and risk tolerance'
    },
    {
      id: 9,
      priority: 'medium',
      title: 'Professional Liability Exclusion',
      section: 'Exclusions',
      description: 'Excludes design-assist work performed by contractor',
      exposure: '$75K',
      recommendation: 'Add professional liability coverage if applicable'
    },
    {
      id: 10,
      priority: 'medium',
      title: 'Subcontractor Coverage Gap',
      section: 'Coverage',
      description: 'No blanket additional insured endorsement for subcontractors',
      exposure: '$100K',
      recommendation: 'Add blanket endorsement for cost efficiency'
    }
  ];

  // Mock analysis results
  const analysisResults = {
    overallRiskScore: 72,
    criticalIssues: criticalIssues.length,
    highPriorityIssues: highPriorityIssues.length,
    mediumPriorityIssues: mediumPriorityIssues.length,
    lowPriorityIssues: 5,
    complianceStatus: 'Failed',
    estimatedExposure: '$2.4M',
    reviewTime: '45 minutes',
    documentType: 'Commercial General Liability Policy',
    policyNumber: 'CGL-2024-001234',
    effectiveDate: '01/01/2024',
    expirationDate: '01/01/2025',
    insurer: 'Construction Insurance Co.',
    namedInsured: 'ABC Construction LLC'
  };

  const documentSections = [
    { id: 'declarations', name: 'Declarations', issues: 2 },
    { id: 'coverage', name: 'Coverage Grants', issues: 3 },
    { id: 'exclusions', name: 'Exclusions', issues: 4 },
    { id: 'conditions', name: 'Conditions', issues: 3 },
    { id: 'endorsements', name: 'Endorsements', issues: 2 }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      setActiveView('analysis');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      case 'high': return <AlertTriangle className="w-4 h-4" />;
      case 'medium': return <Info className="w-4 h-4" />;
      case 'low': return <Info className="w-4 h-4" />;
      default: return <Info className="w-4 h-4" />;
    }
  };

  const UploadView = () => (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Single Document Analysis</h1>
        <p className="text-lg text-gray-600 mb-8">
          Upload an insurance policy or certificate to analyze coverage gaps, compliance issues, and risk exposure
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-8 border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
        <div className="text-center">
          <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload Document</h3>
          <p className="text-gray-600 mb-6">
            Drag and drop your insurance document here, or click to browse
          </p>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileUpload}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
          >
            <FileText className="w-5 h-5 mr-2" />
            Choose File
          </label>
          <p className="text-sm text-gray-500 mt-4">
            Supported formats: PDF, DOC, DOCX (Max 50MB)
          </p>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center mb-3">
            <Shield className="w-6 h-6 text-blue-600 mr-2" />
            <h3 className="font-semibold text-gray-900">Coverage Analysis</h3>
          </div>
          <p className="text-sm text-gray-600">
            Comprehensive review of policy provisions, exclusions, and coverage gaps
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center mb-3">
            <AlertTriangle className="w-6 h-6 text-orange-600 mr-2" />
            <h3 className="font-semibold text-gray-900">Risk Assessment</h3>
          </div>
          <p className="text-sm text-gray-600">
            Priority-based risk scoring with financial exposure calculations
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center mb-3">
            <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
            <h3 className="font-semibold text-gray-900">Compliance Check</h3>
          </div>
          <p className="text-sm text-gray-600">
            Verification against industry standards and contractual requirements
          </p>
        </div>
      </div>
    </div>
  );

  const AnalysisView = () => (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Document Analysis Results</h1>
            <p className="text-gray-600">{uploadedFile?.name}</p>
          </div>
          <div className="flex gap-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
            <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center">
              <Eye className="w-4 h-4 mr-2" />
              View Document
            </button>
          </div>
        </div>
      </div>

      {/* Risk Score Dashboard */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Overall Risk Score</h3>
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-red-600 mb-1">{analysisResults.overallRiskScore}/100</div>
          <div className="text-sm text-gray-600">High Risk</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Critical Issues</h3>
            <AlertTriangle className="w-5 h-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-red-600 mb-1">{analysisResults.criticalIssues}</div>
          <div className="text-sm text-gray-600">Require Immediate Attention</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Estimated Exposure</h3>
            <DollarSign className="w-5 h-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-orange-600 mb-1">{analysisResults.estimatedExposure}</div>
          <div className="text-sm text-gray-600">Potential Financial Risk</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Compliance Status</h3>
            <XCircle className="w-5 h-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-red-600 mb-1">Failed</div>
          <div className="text-sm text-gray-600">Does not meet standards</div>
        </div>
      </div>

      {/* Document Info */}
      <div className="bg-white p-6 rounded-lg shadow border mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">Document Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-gray-500">Policy Type</div>
            <div className="font-medium">{analysisResults.documentType}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Policy Number</div>
            <div className="font-medium">{analysisResults.policyNumber}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Effective Date</div>
            <div className="font-medium">{analysisResults.effectiveDate}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Named Insured</div>
            <div className="font-medium">{analysisResults.namedInsured}</div>
          </div>
        </div>
      </div>

      {/* Document Viewer */}
      {selectedIssue && (
        <div className="bg-white rounded-lg shadow border mb-8">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Document Content</h3>
              <button
                onClick={() => setSelectedIssue(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">{getDocumentContent(selectedIssue.id).title}</p>
          </div>
          <div className="p-6">
            {getDocumentContent(selectedIssue.id).content}
          </div>
        </div>
      )}

      {/* Issues List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Critical Issues */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow border">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Critical Issues</h3>
                <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                  {criticalIssues.length} issues
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {criticalIssues.map((issue) => (
                  <div
                    key={issue.id}
                    className={`p-4 rounded-lg border ${getPriorityColor(issue.priority)} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => setSelectedIssue(issue)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getPriorityIcon(issue.priority)}
                        <h4 className="font-medium">{issue.title}</h4>
                      </div>
                      <span className="text-sm font-semibold">{issue.exposure}</span>
                    </div>
                    <p className="text-sm mb-2">{issue.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Section: {issue.section}</span>
                      <span className="text-xs font-medium">{issue.recommendation}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* High Priority Issues */}
          <div className="bg-white rounded-lg shadow border mt-6">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">High Priority Issues</h3>
                <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full">
                  {highPriorityIssues.length} issues
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {highPriorityIssues.map((issue) => (
                  <div
                    key={issue.id}
                    className={`p-4 rounded-lg border ${getPriorityColor(issue.priority)} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => setSelectedIssue(issue)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getPriorityIcon(issue.priority)}
                        <h4 className="font-medium">{issue.title}</h4>
                      </div>
                      <span className="text-sm font-semibold">{issue.exposure}</span>
                    </div>
                    <p className="text-sm mb-2">{issue.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Section: {issue.section}</span>
                      <span className="text-xs font-medium">{issue.recommendation}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Medium Priority Issues */}
          <div className="bg-white rounded-lg shadow border mt-6">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Medium Priority Issues</h3>
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                  {mediumPriorityIssues.length} issues
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {mediumPriorityIssues.map((issue) => (
                  <div
                    key={issue.id}
                    className={`p-4 rounded-lg border ${getPriorityColor(issue.priority)} cursor-pointer hover:shadow-md transition-shadow`}
                    onClick={() => setSelectedIssue(issue)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getPriorityIcon(issue.priority)}
                        <h4 className="font-medium">{issue.title}</h4>
                      </div>
                      <span className="text-sm font-semibold">{issue.exposure}</span>
                    </div>
                    <p className="text-sm mb-2">{issue.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Section: {issue.section}</span>
                      <span className="text-xs font-medium">{issue.recommendation}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Document Navigation */}
        <div className="bg-white rounded-lg shadow border">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Document Sections</h3>
          </div>
          <div className="p-6">
            <div className="space-y-2">
              {documentSections.map((section) => (
                <button
                  key={section.id}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedSection === section.id
                      ? 'bg-blue-50 border-blue-200 text-blue-900'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setSelectedSection(section.id)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{section.name}</span>
                    {section.issues > 0 && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                        {section.issues}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {activeView === 'upload' ? <UploadView /> : <AnalysisView />}
    </div>
  );
};

export default SingleDocumentCheck;

